import React, { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminAuth';

const fmt = (val) =>
  val ? `₹${Number(val).toLocaleString('en-IN')}` : '—';

const STATUS_STYLE = {
  success:   { bg: '#28a74520', color: '#28a745', border: '#28a74540', label: '✅ Paid'       },
  initiated: { bg: '#17a2b820', color: '#17a2b8', border: '#17a2b840', label: '⏳ In Progress' },
  pending:   { bg: '#FFD60A20', color: '#856404', border: '#FFD60A40', label: '🕐 Pending'    },
  failed:    { bg: '#dc354520', color: '#dc3545', border: '#dc354540', label: '❌ Failed'      },
  refunded:  { bg: '#6c757d20', color: '#6c757d', border: '#6c757d40', label: '↩ Refunded'   },
};

const METHOD_ICONS = {
  razorpay_upi:        '📱 UPI',
  razorpay_netbanking: '🏦 Net Banking',
  razorpay_card:       '💳 Card',
  pay_later:           '🕐 Pay Later',
};

const DealerPaymentsPanel = () => {
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res  = await adminApi.get('/admin/dealer_payments');
      const list = res.data?.data || res.data || [];
      setPayments(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dealer payments.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const FILTERS = [
    { key: 'all',       label: 'All' },
    { key: 'pending',   label: 'Pending' },
    { key: 'initiated', label: 'In Progress' },
    { key: 'success',   label: 'Paid' },
    { key: 'failed',    label: 'Failed' },
  ];

  const filtered = payments.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      String(p.id).includes(q) ||
      (p.dealer_name  || '').toLowerCase().includes(q) ||
      (p.dealer_email || '').toLowerCase().includes(q) ||
      (p.dealer_phone || '').includes(q) ||
      String(p.dealer_listing_id).includes(q);
    const matchFilter = filter === 'all' || p.status === filter;
    return matchSearch && matchFilter;
  });

  // Summary stats
  const stats = {
    total:     payments.length,
    paid:      payments.filter(p => p.status === 'success').length,
    pending:   payments.filter(p => p.status === 'pending' || p.status === 'initiated').length,
    revenue:   payments.filter(p => p.status === 'success').reduce((s, p) => s + (p.amount || 0), 0),
  };

  return (
    <div>
      {/* Stats strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16,
        marginBottom: 24,
      }}>
        {[
          { label: 'Total Payments', value: stats.total,              color: '#003566', prefix: '' },
          { label: 'Paid',           value: stats.paid,               color: '#28a745', prefix: '' },
          { label: 'Pending',        value: stats.pending,            color: '#d97706', prefix: '' },
          { label: 'Revenue Collected', value: fmt(stats.revenue),    color: '#7c3aed', prefix: '' },
        ].map(s => (
          <div key={s.label} style={{
            background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12,
            padding: '16px 20px',
          }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, color: '#999', fontWeight: 700, textTransform: 'uppercase' }}>{s.label}</p>
            <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8,
          padding: '8px 14px', flex: 1, minWidth: 200,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="#999" strokeWidth="2"/>
            <path d="M21 21L16.65 16.65" stroke="#999" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            placeholder="Search by dealer name, email, listing ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ border: 'none', outline: 'none', fontSize: 13, flex: 1, color: '#333' }}
          />
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                padding: '8px 14px', borderRadius: 7, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.2s',
                background: filter === f.key ? '#003566' : '#f5f5f5',
                color:      filter === f.key ? '#FFD60A'  : '#555',
                border:     filter === f.key ? '1px solid #FFD60A' : '1px solid #ddd',
              }}
            >
              {f.label}
              <span style={{
                marginLeft: 6, background: filter === f.key ? '#FFD60A' : '#ddd',
                color: filter === f.key ? '#003566' : '#555',
                borderRadius: '50%', width: 18, height: 18,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 800,
              }}>
                {f.key === 'all' ? payments.length : payments.filter(p => p.status === f.key).length}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={fetchPayments}
          style={{
            padding: '8px 16px', background: '#fff', border: '1px solid #003566',
            borderRadius: 7, color: '#003566', fontWeight: 700, fontSize: 12, cursor: 'pointer',
          }}
        >
          🔄 Refresh
        </button>
      </div>

      {error && (
        <div style={{
          background: '#dc354510', border: '1px solid #dc3545', borderRadius: 8,
          padding: '12px 16px', color: '#dc3545', marginBottom: 16, fontSize: 13,
        }}>
          ⚠️ {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{
            width: 40, height: 40, border: '4px solid #e0e0e0',
            borderTopColor: '#003566', borderRadius: '50%',
            animation: 'spin 0.8s linear infinite', margin: '0 auto 12px',
          }}/>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          <p style={{ color: '#666', margin: 0 }}>Loading payments...</p>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#003566' }}>
                {['#ID', 'Dealer', 'Phone', 'Listing', 'Offer', 'Amount', 'Method', 'Status', 'Date'].map(h => (
                  <th key={h} style={{
                    padding: '12px 14px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700, color: '#FFD60A',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                    whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '48px 20px', textAlign: 'center', color: '#999' }}>
                    <p style={{ fontSize: 32, margin: '0 0 8px' }}>💳</p>
                    <p style={{ margin: 0, fontSize: 15, color: '#333' }}>
                      {payments.length === 0 ? 'No dealer payments yet' : 'No results match your filters'}
                    </p>
                  </td>
                </tr>
              ) : (
                filtered.map((p, idx) => {
                  const ss = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
                  return (
                    <tr key={p.id} style={{
                      borderBottom: '1px solid #f0f0f0',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}
                    >
                      <td style={{ padding: '12px 14px', fontSize: 13, fontWeight: 700, color: '#003566' }}>
                        #{p.id}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <p style={{ margin: '0 0 2px', fontSize: 13, fontWeight: 700, color: '#0a0a0a' }}>
                          {p.dealer_name || '—'}
                        </p>
                        <p style={{ margin: 0, fontSize: 11, color: '#999' }}>
                          {p.dealer_email}
                        </p>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#444' }}>
                        {p.dealer_phone ? `+91 ${p.dealer_phone}` : '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          background: '#003566', color: '#FFD60A',
                          padding: '2px 8px', borderRadius: 4,
                          fontSize: 11, fontWeight: 700,
                        }}>
                          #{p.dealer_listing_id}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 13, color: '#003566', fontWeight: 700 }}>
                        {fmt(p.offer_price)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 800, color: p.status === 'success' ? '#28a745' : '#333' }}>
                        {fmt(p.amount)}
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#555' }}>
                        {METHOD_ICONS[p.payment_method] || p.payment_method || '—'}
                      </td>
                      <td style={{ padding: '12px 14px' }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 6,
                          fontSize: 11, fontWeight: 700,
                          background: ss.bg, color: ss.color, border: `1px solid ${ss.border}`,
                          whiteSpace: 'nowrap',
                        }}>
                          {ss.label}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                        {p.created_at
                          ? new Date(p.created_at).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
                          : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DealerPaymentsPanel;